/**
 * 날짜 및 시간 처리 유틸리티 함수들
 */

/**
 * 한국어 요일 배열
 */
export const KOREAN_WEEKDAYS = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];

/**
 * 한국어 요일 (짧은 형태)
 */
export const KOREAN_WEEKDAYS_SHORT = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * 한국어 월 이름
 */
export const KOREAN_MONTHS = [
  '1월', '2월', '3월', '4월', '5월', '6월',
  '7월', '8월', '9월', '10월', '11월', '12월'
];

/**
 * 한국어 월 이름 (긴 형태)
 */
export const KOREAN_MONTHS_LONG = [
  '1월', '2월', '3월', '4월', '5월', '6월',
  '7월', '8월', '9월', '10월', '11월', '12월'
];

/**
 * 날짜를 한국어 형식으로 포맷팅
 * @param date - 포맷팅할 날짜
 * @param format - 포맷 옵션
 * @returns 포맷팅된 날짜 문자열
 */
export const formatKoreanDate = (
  date: Date | string | number,
  format: 'full' | 'long' | 'medium' | 'short' | 'custom' = 'medium',
  customFormat?: string
): string => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  
  if (format === 'custom' && customFormat) {
    return formatCustomDate(dateObj, customFormat);
  }

  const year = dateObj.getFullYear();
  const month = dateObj.getMonth() + 1;
  const day = dateObj.getDate();
  const weekday = dateObj.getDay();

  switch (format) {
    case 'full':
      return `${year}년 ${month}월 ${day}일 ${KOREAN_WEEKDAYS[weekday]}`;
    case 'long':
      return `${year}년 ${month}월 ${day}일`;
    case 'medium':
      return `${month}월 ${day}일 (${KOREAN_WEEKDAYS_SHORT[weekday]})`;
    case 'short':
      return `${month}/${day}`;
    default:
      return `${year}년 ${month}월 ${day}일`;
  }
};

/**
 * 커스텀 날짜 포맷팅
 * @param date - 날짜 객체
 * @param format - 커스텀 포맷 문자열
 * @returns 포맷팅된 날짜 문자열
 */
const formatCustomDate = (date: Date, format: string): string => {
  return format
    .replace('YYYY', date.getFullYear().toString())
    .replace('YY', date.getFullYear().toString().slice(-2))
    .replace('MM', (date.getMonth() + 1).toString().padStart(2, '0'))
    .replace('M', (date.getMonth() + 1).toString())
    .replace('DD', date.getDate().toString().padStart(2, '0'))
    .replace('D', date.getDate().toString())
    .replace('dddd', KOREAN_WEEKDAYS[date.getDay()])
    .replace('ddd', KOREAN_WEEKDAYS_SHORT[date.getDay()])
    .replace('HH', date.getHours().toString().padStart(2, '0'))
    .replace('H', date.getHours().toString())
    .replace('mm', date.getMinutes().toString().padStart(2, '0'))
    .replace('m', date.getMinutes().toString())
    .replace('ss', date.getSeconds().toString().padStart(2, '0'))
    .replace('s', date.getSeconds().toString());
};

/**
 * 두 날짜 간의 차이를 계산
 * @param startDate - 시작 날짜
 * @param endDate - 종료 날짜
 * @param unit - 차이 단위
 * @returns 차이 값
 */
export const getDateDifference = (
  startDate: Date | string | number,
  endDate: Date | string | number,
  unit: 'days' | 'hours' | 'minutes' | 'seconds' | 'milliseconds' = 'days'
): number => {
  const start = typeof startDate === 'string' || typeof startDate === 'number' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' || typeof endDate === 'number' ? new Date(endDate) : endDate;
  
  const diffInMs = end.getTime() - start.getTime();
  
  switch (unit) {
    case 'days':
      return Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    case 'hours':
      return Math.floor(diffInMs / (1000 * 60 * 60));
    case 'minutes':
      return Math.floor(diffInMs / (1000 * 60));
    case 'seconds':
      return Math.floor(diffInMs / 1000);
    case 'milliseconds':
      return diffInMs;
    default:
      return diffInMs;
  }
};

/**
 * 특정 날짜가 오늘인지 확인
 * @param date - 확인할 날짜
 * @returns 오늘인지 여부
 */
export const isToday = (date: Date | string | number): boolean => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const today = new Date();
  
  return dateObj.getDate() === today.getDate() &&
         dateObj.getMonth() === today.getMonth() &&
         dateObj.getFullYear() === today.getFullYear();
};

/**
 * 특정 날짜가 이번 주인지 확인
 * @param date - 확인할 날짜
 * @returns 이번 주인지 여부
 */
export const isThisWeek = (date: Date | string | number): boolean => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const today = new Date();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  
  return dateObj >= startOfWeek && dateObj <= endOfWeek;
};

/**
 * 특정 날짜가 이번 달인지 확인
 * @param date - 확인할 날짜
 * @returns 이번 달인지 여부
 */
export const isThisMonth = (date: Date | string | number): boolean => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const today = new Date();
  
  return dateObj.getMonth() === today.getMonth() &&
         dateObj.getFullYear() === today.getFullYear();
};

/**
 * 특정 날짜가 이번 년도인지 확인
 * @param date - 확인할 날짜
 * @returns 이번 년도인지 여부
 */
export const isThisYear = (date: Date | string | number): boolean => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const today = new Date();
  
  return dateObj.getFullYear() === today.getFullYear();
};

/**
 * 주어진 날짜의 시작일 (00:00:00)
 * @param date - 날짜
 * @returns 시작일
 */
export const getStartOfDay = (date: Date | string | number): Date => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const startOfDay = new Date(dateObj);
  startOfDay.setHours(0, 0, 0, 0);
  return startOfDay;
};

/**
 * 주어진 날짜의 종료일 (23:59:59)
 * @param date - 날짜
 * @returns 종료일
 */
export const getEndOfDay = (date: Date | string | number): Date => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const endOfDay = new Date(dateObj);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay;
};

/**
 * 주어진 날짜의 주 시작일 (일요일)
 * @param date - 날짜
 * @returns 주 시작일
 */
export const getStartOfWeek = (date: Date | string | number): Date => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const startOfWeek = new Date(dateObj);
  const day = dateObj.getDay();
  startOfWeek.setDate(dateObj.getDate() - day);
  startOfWeek.setHours(0, 0, 0, 0);
  return startOfWeek;
};

/**
 * 주어진 날짜의 주 종료일 (토요일)
 * @param date - 날짜
 * @returns 주 종료일
 */
export const getEndOfWeek = (date: Date | string | number): Date => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const endOfWeek = new Date(dateObj);
  const day = dateObj.getDay();
  endOfWeek.setDate(dateObj.getDate() + (6 - day));
  endOfWeek.setHours(23, 59, 59, 999);
  return endOfWeek;
};

/**
 * 주어진 날짜의 월 시작일
 * @param date - 날짜
 * @returns 월 시작일
 */
export const getStartOfMonth = (date: Date | string | number): Date => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const startOfMonth = new Date(dateObj);
  startOfMonth.setDate(1);
  startOfMonth.setHours(0, 0, 0, 0);
  return startOfMonth;
};

/**
 * 주어진 날짜의 월 종료일
 * @param date - 날짜
 * @returns 월 종료일
 */
export const getEndOfMonth = (date: Date | string | number): Date => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const endOfMonth = new Date(dateObj);
  endOfMonth.setMonth(endOfMonth.getMonth() + 1, 0);
  endOfMonth.setHours(23, 59, 59, 999);
  return endOfMonth;
};

/**
 * 날짜에 일정 기간을 더하거나 뺌
 * @param date - 기준 날짜
 * @param amount - 더하거나 뺄 양
 * @param unit - 단위
 * @returns 계산된 날짜
 */
export const addDate = (
  date: Date | string | number,
  amount: number,
  unit: 'years' | 'months' | 'days' | 'hours' | 'minutes' | 'seconds'
): Date => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const result = new Date(dateObj);
  
  switch (unit) {
    case 'years':
      result.setFullYear(result.getFullYear() + amount);
      break;
    case 'months':
      result.setMonth(result.getMonth() + amount);
      break;
    case 'days':
      result.setDate(result.getDate() + amount);
      break;
    case 'hours':
      result.setHours(result.getHours() + amount);
      break;
    case 'minutes':
      result.setMinutes(result.getMinutes() + amount);
      break;
    case 'seconds':
      result.setSeconds(result.getSeconds() + amount);
      break;
  }
  
  return result;
};

/**
 * 날짜가 유효한지 확인
 * @param date - 확인할 날짜
 * @returns 유효한 날짜인지 여부
 */
export const isValidDate = (date: any): boolean => {
  if (date instanceof Date) {
    return !isNaN(date.getTime());
  }
  
  if (typeof date === 'string' || typeof date === 'number') {
    const parsed = new Date(date);
    return !isNaN(parsed.getTime());
  }
  
  return false;
};

/**
 * 상대적 시간 표현 (예: "3일 전", "1시간 전")
 * @param date - 기준 날짜
 * @param baseDate - 비교할 기준 날짜 (기본값: 현재 시간)
 * @returns 상대적 시간 문자열
 */
export const getRelativeTime = (
  date: Date | string | number,
  baseDate: Date | string | number = new Date()
): string => {
  const targetDate = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const base = typeof baseDate === 'string' || typeof baseDate === 'number' ? new Date(baseDate) : baseDate;
  
  const diffInMs = base.getTime() - targetDate.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInYears > 0) {
    return `${diffInYears}년 전`;
  } else if (diffInMonths > 0) {
    return `${diffInMonths}개월 전`;
  } else if (diffInDays > 0) {
    return `${diffInDays}일 전`;
  } else if (diffInHours > 0) {
    return `${diffInHours}시간 전`;
  } else if (diffInMinutes > 0) {
    return `${diffInMinutes}분 전`;
  } else if (diffInSeconds > 0) {
    return `${diffInSeconds}초 전`;
  } else {
    return '방금 전';
  }
};

/**
 * 로컬 시간대 기준으로 날짜를 YYYY-MM-DD 형식의 문자열로 변환
 * (toISOString()은 UTC 기준이므로 시간대 차이로 날짜가 바뀔 수 있음)
 * @param date - 변환할 날짜
 * @returns YYYY-MM-DD 형식의 날짜 문자열
 */
export const formatLocalDateString = (date: Date | string | number): string => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
